import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Premium Card Component - Swiss Luxury Aesthetic
 * Clean, spacious design with subtle shadows and refined borders
 */
const Card = React.forwardRef(({ className, variant = 'default', ...props }, ref) => {
  const variants = {
    default: 'bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow duration-normal',
    elevated: 'bg-white shadow-lg hover:shadow-xl transition-shadow duration-normal',
    glass: 'bg-white/80 backdrop-blur-sm border border-neutral-200/50 shadow-glass',
    interactive: 'bg-white border-2 border-neutral-200 hover:border-primary-500 hover:shadow-md transition-all duration-normal cursor-pointer',
  };

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl overflow-hidden',
        variants[variant],
        className
      )}
      {...props}
    />
  );
});

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-2 p-8', className)}
    {...props}
  />
));

const CardTitle = React.forwardRef(({ className, as: Component = 'h3', ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      'font-display text-2xl font-bold leading-tight !tracking-display text-neutral-900',
      className
    )}
    {...props}
  />
));

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-neutral-600 leading-relaxed', className)}
    {...props}
  />
));

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-8 pt-0', className)} {...props} />
));

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-8 pt-0', className)}
    {...props}
  />
));

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardTitle.displayName = 'CardTitle';
CardDescription.displayName = 'CardDescription';
CardContent.displayName = 'CardContent';
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
